import * as React from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { subject } from '@casl/ability';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { useQueryClient } from 'react-query';

import { TempControlPdfStatus, TempControlProjectStatus, TempControlValveFamily } from '__generated-api__';
import api from 'api';
import { useAbility } from 'auth';
import { Can } from 'auth/components/Can';
import { useQuery } from 'hooks/query';
import useDocumentTitle from 'hooks/useDocumentTitle';
import Icon from 'components/icon';
import { Button } from 'components/Button';
import { MainHero } from 'my-account/components/MainHero';
import { Modal } from 'my-account/components/Modal';
import { Block403Error } from 'my-account/pages/403page';
import { ErrorMessagesContent, useErrorHandler } from 'my-account/utils/error-handler';
import { getUserName } from 'my-account/utils/user';
import { useToast } from 'my-account/toast';
import { useTempControlProject } from 'tempcontrol/hooks/use-tempcontrol-project';
import DownloadModal, { DownloadFormValues } from 'tempcontrol/results/download-modal';
import { buildWizardUrl } from 'tempcontrol/utils/wizard-url';
import { CALCULATION_METHOD_LABEL, parseCalculationMethod } from 'tempcontrol/utils/calculation-method';
import { resolveResultProducts, selectedResultPartNumber } from 'tempcontrol/utils/resolve-result-products';

const STATUS_LABEL: Record<TempControlProjectStatus, string> = {
  [TempControlProjectStatus.Draft]: 'Draft',
  [TempControlProjectStatus.Completed]: 'Completed',
};

const FAMILY_LABEL: Record<TempControlValveFamily, string> = {
  [TempControlValveFamily.Digital]: 'Digital',
  [TempControlValveFamily.Conventional]: 'Thermostatic',
};

const METHOD_LABEL = CALCULATION_METHOD_LABEL;

const FACILITY_LABELS: Record<string, string> = {
  apartment: 'Apartment / Multi-family',
  hospital: 'Hospital / Healthcare',
  hotel: 'Hotel / Lodging',
  office: 'Office',
  school: 'School / Dormitory',
  club: 'Club / Recreation',
  gym: 'Gym / Locker Room',
  industrial: 'Industrial / Manufacturing',
  maximum_flow: 'Maximum Flow',
};

const FIXTURE_LABELS: ReadonlyArray<string> = [
  'Lavatory Faucet (Private)',
  'Lavatory Faucet (Public)',
  'Bathtub',
  'Therapeutic Bath',
  'Kitchen Sink',
  'Pantry Sink',
  'Service Sink',
  'Shower',
  'Circular Wash Fountain',
  'Semi-Circular Wash Fountain',
  'Residential Dishwasher',
  'Commercial Dishwasher',
  'Residential Washing Machine',
  'Commercial Washing Machine',
];

const formatDate = (value: string | null | undefined) => {
  if (!value) return null;
  try {
    return format(parseISO(value), 'MMM d, yyyy');
  } catch {
    return null;
  }
};

const formatNumber = (value: number | undefined | null, fractionDigits = 1) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

/**
 * SPB-style human label for the PDF lifecycle. Returned `undefined`
 * for the no-op `idle` state so we don't pollute the status card on
 * projects that have never had a download requested.
 */
const pdfStatusToLabel = (status: TempControlPdfStatus | undefined | null): string | undefined => {
  switch (status) {
    case TempControlPdfStatus.Queued:
      return 'Queued for rendering';
    case TempControlPdfStatus.Generating:
      return 'Rendering on the server';
    case TempControlPdfStatus.Ready:
      return 'Ready';
    case TempControlPdfStatus.Failed:
      return 'Last render failed; click Regenerate to retry';
    case TempControlPdfStatus.Idle:
    default:
      return undefined;
  }
};

export default function TempControlProjectDetailPage() {
  const { key } = useParams<{ key: string }>();
  const ability = useAbility();
  const history = useHistory();
  const queryClient = useQueryClient();
  const toast = useToast();
  const errorHandler = useErrorHandler();
  const projectApi = useTempControlProject();

  const [projectRes, getProjectState] = useQuery(api.tempcontrol.getTempControlProject, { key });
  const project = projectRes?.data;

  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = React.useState(false);
  const [isPdfRendering, setIsPdfRendering] = React.useState(false);
  const [isStreaming, setIsStreaming] = React.useState(false);

  useDocumentTitle(project?.project_name ? `${project.project_name} — TempControl` : 'TempControl Project');

  const canViewList = ability.can('list', 'TempControlProject');
  const isPdfReady = project?.pdf_status === TempControlPdfStatus.Ready;
  const pdfStatusLabel = pdfStatusToLabel(project?.pdf_status);

  // Which valve family the customer selected, and the model that matches it.
  // The engine stores the thermostatic part number on `result.part_number`;
  // when Digital is selected we swap in the digital SKU via the same mapping
  // the wizard results page and the PDF use, so the portal reflects the
  // actual choice instead of always showing the thermostatic model.
  const selectedFamily =
    project?.inputs?.family === TempControlValveFamily.Digital
      ? TempControlValveFamily.Digital
      : TempControlValveFamily.Conventional;
  const resolvedProducts = project?.result ? resolveResultProducts(project.result) : null;
  const displayPartNumber = resolvedProducts
    ? selectedResultPartNumber(resolvedProducts, selectedFamily)
    : project?.result?.part_number ?? 'No matching valve';

  const invalidate = React.useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries(api.tempcontrol.listTempControlProjects.getQueryKey()[0]),
      queryClient.invalidateQueries(api.tempcontrol.getTempControlProject.getQueryKey({ key })[0]),
    ]);
  }, [queryClient, key]);

  const handleDelete = React.useCallback(async () => {
    try {
      await projectApi.deleteProject(key);
      await invalidate();
      toast.notify({
        type: 'success',
        title: 'Project deleted',
        message: 'The project has been removed from your list.',
        autoClose: true,
      });
      if (canViewList) {
        history.push('/tempcontrol-projects');
      }
    } catch (e) {
      errorHandler.handleError(e);
    }
  }, [canViewList, errorHandler, history, invalidate, key, projectApi, toast]);

  /**
   * Trigger the browser download for a blob the API just handed us.
   * Shared by every download path (cached stream / re-render trigger
   * / lead-capture modal) so the anchor-click + revoke pattern lives
   * in exactly one place.
   */
  const triggerBrowserDownload = React.useCallback((blob: Blob, filename: string) => {
    const url = (window.URL || window.webkitURL).createObjectURL(blob);
    const link = document.createElement('a');
    link.rel = 'noopener';
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => (window.URL || window.webkitURL).revokeObjectURL(url), 40000);
  }, []);

  /**
   * Re-render-and-download path used by the lead-capture modal.
   * Reached only on the rare case where a portal user opens a
   * project that has never had customer info captured (typically a
   * draft an admin is poking at). For the common case the
   * `handlePortalDownloadClick` handler below skips the modal and
   * reuses the customer info already on the project.
   */
  const handleDownload = React.useCallback(
    async (values: DownloadFormValues) => {
      setIsPdfRendering(true);
      try {
        const { blob, filename } = await projectApi.downloadProject(key, {
          customer_name: values.customer_name,
          customer_email: values.customer_email,
          customer_company: values.customer_company || undefined,
        });
        triggerBrowserDownload(blob, filename);

        await invalidate();
        setIsDownloadOpen(false);
        toast.notify({
          type: 'success',
          title: 'Your PDF is ready',
          message: 'We saved your details on this project so you can re-download later.',
          autoClose: true,
        });
      } finally {
        setIsPdfRendering(false);
      }
    },
    [invalidate, key, projectApi, toast, triggerBrowserDownload]
  );

  /**
   * Customer-portal click handler for the single Download button.
   *
   * Three cases, no popup unless we genuinely need lead info:
   *
   *   1. Cached PDF (`pdf_status === 'ready'`): stream straight from
   *      the private filesystem - the lead was captured at the
   *      original wizard download.
   *   2. Re-render needed and customer info is already on the
   *      project: trigger a fresh render with the existing values,
   *      poll, stream. No modal - the lead is already captured.
   *   3. Re-render needed and customer info is missing (project
   *      never went through the wizard's download flow): fall back
   *      to the lead-capture modal so the FormRequest's required
   *      `customer_email` validation can pass.
   */
  const handlePortalDownloadClick = React.useCallback(async () => {
    if (!project) {
      return;
    }

    if (isPdfReady) {
      setIsStreaming(true);
      try {
        const { blob, filename } = await projectApi.streamProjectPdf(key);
        triggerBrowserDownload(blob, filename);
      } catch (e) {
        errorHandler.handleError(e);
      } finally {
        setIsStreaming(false);
      }
      return;
    }

    const hasLead = !!project.customer_name && !!project.customer_email;
    if (!hasLead) {
      setIsDownloadOpen(true);
      return;
    }

    setIsPdfRendering(true);
    try {
      const { blob, filename } = await projectApi.downloadProject(key, {
        customer_name: project.customer_name as string,
        customer_email: project.customer_email as string,
        customer_company: project.customer_company || undefined,
      });
      triggerBrowserDownload(blob, filename);
      await invalidate();
      toast.notify({
        type: 'success',
        title: 'Your PDF is ready',
        message: 'Generated a fresh PDF using the customer details on this project.',
        autoClose: true,
      });
    } catch (e) {
      errorHandler.handleError(e);
    } finally {
      setIsPdfRendering(false);
    }
  }, [errorHandler, invalidate, isPdfReady, key, project, projectApi, toast, triggerBrowserDownload]);

  return (
    <>
      <MainHero />

      <section className="c-block c-block--hero">
        <div className="o-container-fluid">
          <div className="o-row u-flex-grow u-items-end">
            <div className="o-col-12">
              {canViewList && (
                <Link
                  to="/tempcontrol-projects"
                  className="c-link-cta-basic c-link-cta--small c-block__get-back u-mt-spacer-base-large u-mb-spacer-base-small"
                >
                  <Icon name="arrow" className="o-svg-icon o-svg-left" />
                  <span>Back to TempControl projects</span>
                </Link>
              )}
              <h1 className="c-title--medium u-mb-0">{project?.project_name ?? 'TempControl Project'}</h1>
            </div>
          </div>
        </div>
        <div className="c-block__pattern c-block__pattern--light3 c-block__pattern--top3 c-block__overlay--opacity-20">
          <Icon name="pattern" className="o-svg-icon" />
        </div>
      </section>

      {getProjectState.isLoading && (
        <div className="c-listing__none">
          <div className="c-listing__none-spinner"></div>
          <p className="c-listing__none-title">Loading…</p>
        </div>
      )}

      {getProjectState.isError && (
        <div className="o-container-fluid u-pb-spacer-section">
          <div className="o-row u-flex-grow u-items-center">
            <div className="o-col-12">
              <div className="c-listing__none">
                <div className="c-listing__none-figure">
                  <Icon name="error" className="o-svg-icon" />
                </div>
                <p className="c-listing__none-title">Error</p>
                <ErrorMessagesContent error={getProjectState.error} />
              </div>
            </div>
          </div>
        </div>
      )}

      {project && getProjectState.isSuccess && (
        <>
          {ability.can('read', subject('TempControlProject', project)) ? (
            <section className="c-block c-block--spacing-t-small c-block--spacing-b">
              <div className="o-container-fluid">
                <div className="o-row">
                  <div className="o-col-12 o-col-8@md">
                    <SummaryCard title="Recommended Valve">
                      {project.result ? (
                        <>
                          <p className="c-headline u-mb-spacer-base-small">
                            {FAMILY_LABEL[selectedFamily]} valve · Part number
                          </p>
                          <p className="c-title--s u-mb-spacer-base">{displayPartNumber}</p>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                              gap: '1rem',
                              marginTop: '1rem',
                            }}
                          >
                            <Stat
                              label="Valve Configuration"
                              value={
                                (project.result.manifold_count ?? 0) > 0
                                  ? `${(project.result.manifold_count ?? 0) + 1} valves in parallel`
                                  : 'Single valve'
                              }
                            />
                            <Stat
                              label="Total Fixture Units"
                              value={formatNumber(project.result.total_fixture_units)}
                            />
                            <Stat label="Total Flow (GPM)" value={formatNumber(project.result.total_flow_gpm)} />
                            <Stat
                              label="Actual Pressure Drop"
                              value={`${formatNumber(project.result.actual_press_drop_psi)} PSI`}
                            />
                            {project.result.ips_label && (
                              <Stat label="IPS Connections (Inlet x Outlet)" value={project.result.ips_label} />
                            )}
                          </div>
                        </>
                      ) : (
                        <p className="c-note">No calculation has been run yet. Open in the wizard to finish sizing.</p>
                      )}
                    </SummaryCard>

                    <SummaryCard title="Inputs">
                      {project.inputs ? (
                        <dl className="c-data-pairs" style={{ display: 'grid', gap: '0.5rem' }}>
                          <Pair label="Facility Type">
                            {(project.inputs.facility && FACILITY_LABELS[project.inputs.facility]) ||
                              project.inputs.facility ||
                              '—'}
                          </Pair>
                          <Pair label="Valve Type">
                            {(project.inputs.family && FAMILY_LABEL[project.inputs.family]) ||
                              project.inputs.family ||
                              '—'}
                          </Pair>
                          <Pair label="Allowable PSI Drop">
                            {project.inputs.allowable_psi ? `${project.inputs.allowable_psi} PSI` : '—'}
                          </Pair>
                        </dl>
                      ) : (
                        <p className="c-note">No inputs yet.</p>
                      )}
                    </SummaryCard>

                    <SummaryCard title="Fixtures">
                      {project.inputs?.fixtures && project.inputs.fixtures.some((f) => f.quantity > 0) ? (
                        <ul className="c-list-bullets" style={{ listStyle: 'none', padding: 0 }}>
                          {project.inputs.fixtures
                            .filter((f) => f.quantity > 0)
                            .map((row) => (
                              <li
                                key={row.type}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  padding: '0.375rem 0',
                                  borderBottom: '1px solid var(--color-border, #d8d8d8)',
                                }}
                              >
                                <span>{FIXTURE_LABELS[row.type] ?? `Fixture ${row.type}`}</span>
                                <strong>{row.quantity}</strong>
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <p className="c-note">No fixtures specified.</p>
                      )}
                    </SummaryCard>
                  </div>

                  <div className="o-col-12 o-col-4@md">
                    <aside className="u-flex u-flex-column" style={{ gap: '1rem' }}>
                      <SummaryCard title="Status">
                        <p className="u-mb-spacer-base-small">
                          <strong>{STATUS_LABEL[project.status]}</strong>
                          {project.method ? ` · ${METHOD_LABEL[parseCalculationMethod(project.method)]} method` : ''}
                        </p>
                        <p className="c-note">Created {formatDate(project.created_at) ?? '—'}</p>
                        {project.last_downloaded_at && (
                          <p className="c-note">Last download {formatDate(project.last_downloaded_at)}</p>
                        )}
                        {pdfStatusLabel && <p className="c-note">PDF: {pdfStatusLabel}</p>}
                      </SummaryCard>

                      <SummaryCard title="Customer">
                        <Pair label="Name">{project.customer_name ?? '—'}</Pair>
                        <Pair label="Email">{project.customer_email ?? '—'}</Pair>
                        <Pair label="Company">{project.customer_company ?? '—'}</Pair>
                      </SummaryCard>

                      {project.user && (
                        <SummaryCard title="Owner">
                          <p className="u-mb-0">{getUserName(project.user).name}</p>
                        </SummaryCard>
                      )}

                      <div className="u-flex u-flex-column" style={{ gap: '0.5rem' }}>
                        {/* The PDF endpoints all 422 when the project has no
                            calculation result, so we hide the CTA entirely
                            for never-calculated drafts. The Recommended
                            valve card upstream already prompts the user to
                            "Open in wizard to finish sizing", and the
                            "Open in wizard" button right below this is the
                            obvious next step. */}
                        {project.result && (
                          <Button
                            variant="primary"
                            onClick={handlePortalDownloadClick}
                            rightIcon="download"
                            isLoading={isStreaming || projectApi.isDownloading || isPdfRendering}
                          >
                            {isPdfReady
                              ? 'Download PDF'
                              : project.pdf_status === TempControlPdfStatus.Failed
                              ? 'Regenerate PDF'
                              : project.last_downloaded_at
                              ? 'Generate PDF'
                              : 'Download PDF'}
                          </Button>
                        )}

                        <Can I="update" this={subject('TempControlProject', project)}>
                          {/* The public wizard lives on the WP root domain,
                              outside the my-account `BrowserRouter` basename;
                              use a real anchor so we navigate cleanly. */}
                          <a href={buildWizardUrl(project.key)} className="c-button c-button--white">
                            <Icon name="edit" className="o-svg-icon o-svg-left" />
                            <span>Open in wizard</span>
                          </a>
                        </Can>

                        <Can I="delete" this={subject('TempControlProject', project)}>
                          <Button
                            variant="white"
                            onClick={() => setIsDeleteOpen(true)}
                            leftIcon="trash"
                            isLoading={projectApi.isDeleting}
                          >
                            Delete project
                          </Button>
                        </Can>
                      </div>
                    </aside>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <Block403Error />
          )}
        </>
      )}

      <Modal
        isOpen={isDeleteOpen}
        onRequestClose={projectApi.isDeleting ? () => undefined : () => setIsDeleteOpen(false)}
        id="tempcontrol-detail-delete-confirm"
      >
        <div className="c-modal c-modal--contact">
          {/* eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/anchor-is-valid */}
          <a
            href="#"
            className="c-modal-close c-modal-close--invert"
            onClick={(e) => {
              e.preventDefault();
              if (!projectApi.isDeleting) setIsDeleteOpen(false);
            }}
          />
          <div className="c-modal__main">
            <div className="c-block c-block--spacing-t-small c-block--spacing-b-small c-color--white">
              <div className="o-container-fluid">
                <div className="c-block__header">
                  <h5 className="c-title--large">
                    Delete <strong>this project?</strong>
                  </h5>
                  <p>The project will be removed from your list. This action can't be undone from the portal.</p>
                </div>
                <div className="u-flex u-items-center u-flex-wrap" style={{ gap: '0.75rem' }}>
                  <Button
                    variant="primary"
                    isLoading={projectApi.isDeleting}
                    onClick={async () => {
                      await handleDelete();
                      setIsDeleteOpen(false);
                    }}
                  >
                    Yes, delete
                  </Button>
                  <button
                    type="button"
                    className="c-link-cta-basic"
                    onClick={() => setIsDeleteOpen(false)}
                    disabled={projectApi.isDeleting}
                  >
                    <Icon name="close" className="o-svg-icon o-svg-left" />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {project && (
        <DownloadModal
          isOpen={isDownloadOpen}
          isSubmitting={projectApi.isDownloading || isPdfRendering}
          isRendering={isPdfRendering}
          defaultValues={{
            customer_name: project.customer_name ?? '',
            customer_email: project.customer_email ?? '',
            customer_company: project.customer_company ?? '',
          }}
          onRequestClose={() => setIsDownloadOpen(false)}
          onSubmit={handleDownload}
        />
      )}
    </>
  );
}

const SummaryCard: React.FC<{ title: string }> = ({ title, children }) => (
  <div
    className="u-mb-spacer-base"
    style={{
      border: '1px solid var(--color-border, #d8d8d8)',
      borderRadius: '4px',
      padding: '1.25rem',
      background: 'rgba(0, 0, 0, 0.02)',
    }}
  >
    <h3 className="c-title--xs u-mb-spacer-base-small">{title}</h3>
    {children}
  </div>
);

const Stat: React.VFC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="c-note u-mb-0">{label}</p>
    <p className="c-title--xxs u-mb-0">{value}</p>
  </div>
);

const Pair: React.FC<{ label: string }> = ({ label, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', padding: '0.25rem 0' }}>
    <span className="c-note">{label}</span>
    <span style={{ fontWeight: 600 }}>{children}</span>
  </div>
);
